<template>

  <table-tools :actions="tableActions" :headers="headers" v-model:enabled-headers="enabledHeaders" remember-headers="user-index-new">

    <table-filter v-if="canManageOrganizations">
      <o-autocomplete
        :label="$t('titles.organization')"
        v-model.number="filters.organizationID"
        :items="Organization.getOptions()"
        clearable
      />
    </table-filter>

    <table-filter>
      <o-text-field
        :label="$t('titles.username_or_email_address')"
        v-model="filters.identifier"
        clearable
      />
    </table-filter>

    <table-filter>
      <o-text-field
        :label="$t('titles.name')"
        v-model="filters.name"
        clearable
      />
    </table-filter>

    <table-filter>
      <!-- TODO: i18n items -->
      <o-select
        :label="$t('titles.role')"
        :items="objectToItems(UserRoles)"
        v-model="filters.role"
        clearable
      />
    </table-filter>

    <table-filter>
      <o-select
        :label="$t('titles.status')"
        v-model="filters.live"
        :items="[{title: $t('misc.live'), value: true}, {title: $t('misc.onboarded'), value: false }]"
        clearable
      />
    </table-filter>

  </table-tools>

  <v-data-table
    ref="dataTable"
    :headers="visibleHeaders"
    :items="users"
    :sort-by="sortBy"
    v-model:page="page"
    :items-per-page="itemsPerPage"
    :loading="loadingUsers"
  >

    <template v-slot:item.email="{ item }">
      <a v-if="item.email" :href="'mailto:' + item.email">
        {{ item.email }}
      </a>
    </template>

    <template v-slot:item.role="{ item }">
      {{ getEnumLabel(item.role, UserRoles) }}
    </template>

    <template v-slot:item.live="{ item }">
      <template v-if="item.live">
        Live
      </template>
      <template v-if="!item.live">
        Onboarded
      </template>
    </template>

    <template v-slot:item.actions="{ item }">
      <action-group
        :actions="[
          {label: 'Edit', icon: '$edit', to: {name: 'userDetail', params: {userID: item.ID}}},
          {label: 'Solutions', icon: '$solution', to: {name: 'userSolutions', params: {userID: item.ID}}},
        ]"
      />
    </template>

    <template v-slot:bottom>
      <data-table-pagination
        :items="users"
        v-model:page="page"
        v-model:items-per-page="itemsPerPage"
        @update:page="_ => dataTable?.$el.scrollIntoView()"
      />
    </template>

  </v-data-table>
  
</template>

<script setup lang="ts">

import { computed, inject, ref, reactive, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTitle } from '@vueuse/core'

// components
import DataTablePagination from '../../DataTablePagination.vue'

// models
import Organization from '../../../models/Organization'
import User from '../../../models/User'

// enums
import { UserRoles } from '../../../enums'

// misc
import NotificationService from '../../../services/NotificationService'
import getEnumLabel from '../../../utils/getEnumLabel'
import objectToItems from '../../../utils/objectToItems'
import { QuerySyncer, BooleanSerializer, NumberSerializer } from '../../../utils/QuerySyncer'
import type { Can } from '../../../main'
import type { Model } from '@vuex-orm/core'

const { t } = useI18n()
const title = useTitle()
title.value = t('titles.users')

const $can = inject('$can') as Can
const canManageOrganizations = $can('organizations.manage')

/**
 * Data table configs
 */

const dataTable = useTemplateRef('dataTable')

const sortBy = ref([{ key: 'username' }])

const headers = [
  {title: t('titles.id'), key: 'ID', width: 100, hidden: !$can('seeids')},
  {title: t('titles.organization'), key: 'organization.name', width: 300, hidden: !canManageOrganizations},
  {title: t('titles.username'), key: 'username', toggleable: false},
  {title: t('titles.first_name'), key: 'firstName'},
  {title: t('titles.last_name'), key: 'lastName'},
  {title: t('titles.phone_number'), key: 'phone'},
  {title: t('titles.email_address'), key: 'email', width: 300},
  {title: t('titles.role'), key: 'role', width: 140},
  {title: t('titles.status'), key: 'live', width: 140},
  {title: '', key: 'actions', sortable: false, width: 100, hideFromPicker: true},
]

const enabledHeaders = ref([
  'ID', 'organization.name', 'username', 'firstName', 'lastName', 'phone', 'email', 'role', 'live', 'actions',
])

const visibleHeaders = computed(() => {
  return headers.filter((header) => {
    return !header.hidden && enabledHeaders.value.includes(header.key)
  })
})

const filters = reactive({
  organizationID: null,
  identifier: '',
  name: '',
  role: null,
  live: true,
})

new QuerySyncer({
  organization_id: {
    watchable: () => filters.organizationID,
    hydrate: (newValue) => filters.organizationID = newValue,
    serializer: NumberSerializer,
  },
  identifier: {
    watchable: () => filters.identifier,
    hydrate: (newValue) => filters.identifier = newValue,
  },
  name: {
    watchable: () => filters.name,
    hydrate: (newValue) => filters.name = newValue,
  },
  role: {
    watchable: () => filters.role,
    hydrate: (newValue) => filters.role = newValue,
  },
  live: {
    watchable: () => filters.live,
    hydrate: (newValue) => filters.live = newValue,
    serializer: BooleanSerializer,
  },
})

const tableActions = computed(() => [
  {
    title: t('actions.reload'),
    icon: '$reload',
    click: loadUsers,
    busy: loadingUsers.value,
  },
  {
    title: t('actions.create_user'),
    icon: '$create',
    to: {name: 'userDetail', params: {userID: 'new'}},
  },
])

/**
 * Pagination
 */

const page = ref(1)
const itemsPerPage = ref(10)

// when anything that affects the main query is changed, reset the page to 1
watch(filters, () => page.value = 1)

/**
 * User data
 */

const loadingUsers = ref<boolean>(false)

function loadUsers() {

  // make sure we have up-to-date user data
  
  loadingUsers.value = true

  User.fetchAll()
    .catch((error) => {
      console.error(error)
      NotificationService.error(t('alerts.couldnt_load_users'))
    })
    .finally(() => {
      loadingUsers.value = false
    })

}

const users = computed(() => {
    
  const query = User.query().with('organization')

  if (filters.live === true || filters.live === false) {
    query.where('live', filters.live)
  }

  if (filters.organizationID !== null) {
    query.where('organizationID', filters.organizationID)
  }

  if (filters.identifier !== null && filters.identifier.length > 0) {
    query.where((user: Model) => {
      const uppercasedIdentifier = filters.identifier.toUpperCase()
      if ((user as User).username.toUpperCase().indexOf(uppercasedIdentifier) !== -1) {
        return true
      }
      if ((user as User).email.toUpperCase().indexOf(uppercasedIdentifier) !== -1) {
        return true
      }
      return false
    })
  }

  if (filters.name !== null && filters.name.length > 0) {
    query.where((user: Model) => {
      const uppercasedName = filters.name.toUpperCase()
      if ((user as User).firstName.toUpperCase().indexOf(uppercasedName) !== -1) {
        return true
      }
      if ((user as User).lastName.toUpperCase().indexOf(uppercasedName) !== -1) {
        return true
      }
      return false
    })
  }

  if (filters.role) {
    query.where('role', filters.role)
  }

  return query.get()

})

</script>
